<script setup lang="ts">
import { useConnectivity } from '@/composables/useConnectivity';

const { isOnline, isSlowConnection, connectionQuality, checkConnectivity } = useConnectivity();

const dismissed = ref(false);
const checking = ref(false);
const retryFailed = ref(false);
const shaking = ref(false);
const previousQuality = ref(connectionQuality.value);
let shakeTimer: ReturnType<typeof setTimeout> | null = null;

watch(connectionQuality, (quality) => {
  if (quality !== previousQuality.value) {
    dismissed.value = false;
    retryFailed.value = false;
    previousQuality.value = quality;
  }
});

const showBanner = computed(
  () => !dismissed.value && (connectionQuality.value === 'offline' || connectionQuality.value === 'slow'),
);

const bannerText = computed(() => {
  if (!isOnline.value) {
    if (retryFailed.value) return 'Still offline. Check your connection and try again.';
    return 'You\'re offline. Some features may be unavailable.';
  }
  if (isSlowConnection.value) return 'Slow connection detected. Things may take longer than usual.';
  return '';
});

const bannerClass = computed(() =>
  !isOnline.value ? 'connectivity-banner--offline' : 'connectivity-banner--slow',
);

const bannerIcon = computed(() =>
  !isOnline.value ? 'pi pi-wifi' : 'pi pi-exclamation-triangle',
);

async function retry() {
  if (checking.value) return;
  checking.value = true;
  try {
    const reachable = await checkConnectivity();
    if (!reachable) {
      retryFailed.value = true;
      // Re-trigger the shake by toggling the class off then back on
      shaking.value = false;
      if (shakeTimer) clearTimeout(shakeTimer);
      await nextTick();
      shaking.value = true;
      shakeTimer = setTimeout(() => { shaking.value = false; }, 500);
    } else {
      retryFailed.value = false;
    }
  } finally {
    checking.value = false;
  }
}

onBeforeUnmount(() => {
  if (shakeTimer) clearTimeout(shakeTimer);
});
</script>

<template>
  <Transition name="slide-down">
    <div v-if="showBanner"
      :class="['connectivity-banner', bannerClass, { 'connectivity-banner--shake': shaking }]">
      <div class="connectivity-banner__content">
        <i :class="[bannerIcon, 'connectivity-banner__icon']" />
        <span class="connectivity-banner__text">{{ bannerText }}</span>
        <button v-if="!isOnline" class="connectivity-banner__btn"
          :disabled="checking"
          @click="retry">
          <i v-if="checking" class="pi pi-spin pi-spinner" />
          {{ checking ? 'Checking…' : 'Retry' }}
        </button>
        <button class="connectivity-banner__dismiss" aria-label="Dismiss" @click="dismissed = true">
          <i class="pi pi-times" />
        </button>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.connectivity-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10000;
  padding: 0.625rem 1rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  color: #fff;
}

.connectivity-banner--offline {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.connectivity-banner--slow {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.connectivity-banner__content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  max-width: 900px;
  margin: 0 auto;
  font-size: 0.875rem;
}

.connectivity-banner__icon {
  font-size: 1.1rem;
}

.connectivity-banner__text {
  font-weight: 500;
}

.connectivity-banner__btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: #fff;
  padding: 0.3rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 600;
  transition: background 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.connectivity-banner__btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.35);
}

.connectivity-banner__btn:disabled {
  opacity: 0.7;
  cursor: progress;
}

/* Brief shake when a retry attempt confirms we're still offline */
.connectivity-banner--shake {
  animation: shake 0.45s ease;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-6px); }
  40% { transform: translateX(6px); }
  60% { transform: translateX(-3px); }
  80% { transform: translateX(3px); }
}

.connectivity-banner__dismiss {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  transition: color 0.2s;
}

.connectivity-banner__dismiss:hover {
  color: #fff;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>
